import pausedSubLogoRebranding from 'img/subscriptions/pause-subscription-logo-rebranding.png';
import { type BillingDataPlatform, BillingDataPartner } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import {
    graceSubscriptionSidebarDetail,
    hidePeriodFromSubscriptionDetail,
} from 'reactApp/appHelpers/featuresHelpers/features/graceSubscriptions';
import { MultiSessionDeviceSubs } from 'reactApp/appHelpers/featuresHelpers/features/multiSessionSubscriptions';
import { getPromoIcon } from 'reactApp/modules/billingData/billingData.links';
import {
    type BillingDataSubscriptionInfo,
    BillingDataPromoSubscriptionType,
    BillingDataSubscriptionInfoStatus,
    isImapSubscription,
    isTBankSubscription,
} from 'reactApp/modules/billingData/billingData.types';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import {
    isFamilyPlaceByMemberSubscription,
    isGraceSubscription,
    isVkMusicSubscription,
} from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import type { Product } from 'reactApp/types/Billing';
import { formatDefaultDate, formatFileHistoryDate } from 'reactApp/utils/formatDate';
import { getPeriodName } from 'reactApp/utils/Period';
import { computersNominative } from 'reactApp/utils/pluralHelpers';

export const getDescription = ({
    expires,
    autorenewal,
    renewAt,
    status,
    paused,
    isGrace,
}: {
    expires?: number;
    autorenewal?: boolean;
    renewAt?: number;
    status?: string;
    paused?: boolean;
    isGrace?: boolean;
}) => {
    if (paused && renewAt) {
        return `Оплата на паузе до ${formatFileHistoryDate(renewAt)}`;
    }

    if (status === BillingDataSubscriptionInfoStatus.FINISHED) {
        return 'Завершена';
    }

    if (graceSubscriptionSidebarDetail && isGrace && expires) {
        return `Пробуем списать оплату до\u00A0${formatDefaultDate(expires)}`;
    }

    if (autorenewal && renewAt && !isGrace) {
        return `Списание ${formatDefaultDate(renewAt)}`;
    } else if (expires) {
        return `Действует до ${formatDefaultDate(expires)}`;
    }

    return 'Подключено';
};

export const composeContent = ({
    subscription,
    product,
    pausedSubscription,
}: {
    subscription: BillingDataSubscriptionInfo;
    product?: Product;
    pausedSubscription: boolean;
}) => {
    const { autorenewal, renewAt, expires, isTrial, space, partner, status } = subscription;
    const TBankSubscription = isTBankSubscription(subscription);
    const isGrace = isGraceSubscription(subscription);
    const period = (isTrial && !product?.isForceDiscountTrial && product?.trialPeriod) || product?.period;
    const periodText = period ? ` на ${getPeriodName(period, false, true)}` : '';
    const conditionalPeriodText = hidePeriodFromSubscriptionDetail ? '' : periodText;
    const subscriptionType = subscription.type as BillingDataPromoSubscriptionType | BillingDataPlatform;
    const showWaiting = [BillingDataSubscriptionInfoStatus.FINISHED, BillingDataSubscriptionInfoStatus.SUSPENDED].includes(
        status as BillingDataSubscriptionInfoStatus
    );
    const isFamilyPlaceByMember = isFamilyPlaceByMemberSubscription(subscription);
    const prefixText = isFamilyPlaceByMember ? 'В семейном тарифе' : 'Подарок';

    let title = `${space.value}${conditionalPeriodText}`;
    let icon = subscription.icon;

    if (subscriptionType === BillingDataPromoSubscriptionType.quota) {
        title = TBankSubscription ? `Mail Space ${space.value}` : `${prefixText} ${space.value}${conditionalPeriodText}`;
        // tempexp_16598-next-line
    } else if (product?.id && Object.values(cheapestProductIDs).includes(product.id)) {
        title = 'Тариф: Без рекламы';
    }

    if (isVkMusicSubscription(subscription)) {
        title = `${space.value} и VK Музыка${periodText}`;
        icon = getPromoIcon(BillingDataPartner.vkmusic);
    }

    /* tempexp_20439-start */
    if (product?.id && MultiSessionDeviceSubs.enabled) {
        if (MultiSessionDeviceSubs.isFreeChoiceProduct(product.id)) {
            const devices = MultiSessionDeviceSubs.getDeviceCount(product.id);
            title = `${devices} ${computersNominative(devices)}`;
        }
        if (MultiSessionDeviceSubs.isPresetInSubsProdcut(product.id)) {
            title = 'Тариф Для работы';
        }
    }
    /* tempexp_20439-end */

    /* tempexp_20582-start */
    if (isImapSubscription(subscription)) {
        title = 'Создание паролей';
    }
    /* tempexp_20582-end */

    return {
        title,
        description: TBankSubscription
            ? `Подключена через Т-Банк, действует до\u00A0${formatDefaultDate(Number(expires))}`
            : getDescription({
                  autorenewal,
                  expires,
                  renewAt,
                  status,
                  isGrace,
                  paused: pausedSubscription,
              }),
        icon: pausedSubscription ? pausedSubLogoRebranding : TBankSubscription ? getPromoIcon(BillingDataPartner.tbank) : icon,
        partner,
        showWaiting,
    };
};
